import {HttpBaseService} from "./http-base.service";
import {environment} from "../../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {MessageService} from "./message.service";
import {inject, Injectable} from "@angular/core";
import {MisplannedCapacityEnum} from "../../models/Enums/MisplannedCapacityEnum";
import {EnumMapper} from "./enumMapper.service";
import {UntypedFormControl} from "@angular/forms";
import {CustomizingService} from "./customizing.service";
import moment from "moment";
import {
	EmployeeCapacityListElement,
	EmployeeRevenueListElement,
} from "../../../shared/capacity-list/employee-capacity-list/employee-capacity-list.interface";
import {CapacityParameters} from "../../../shared/capacity-list/employee-capacity-legend/employee-capacity-legend.component";
import {TranslateService} from "@ngx-translate/core";
import {firstValueFrom, Observable, switchMap} from "rxjs";
import {Client} from "../../config/Client";
import {DivisionService} from "./division.service";
import {asyncMap} from "../../util/util";

@Injectable({
	providedIn: "root",
})
export class EmployeeCapacityService extends HttpBaseService {
	public http = inject(HttpClient);
	public messageService = inject(MessageService);
	private readonly customizingService = inject(CustomizingService);
	private readonly translateService = inject(TranslateService);
	private readonly divisionService = inject(DivisionService);

	url: string = environment.baseUrl + "api/employees/capacityOverview";
	urlNN: string = environment.baseUrl + "api/employees/NNemployees";

	constructor() {
		super();
	}

	public getEmployeeCapacities(
		capacityParameters: CapacityParameters,
		businessUnits: string[],
		divisions: string[],
		departments: string[],
		statuses: string[],
		searchString: string,
		includeBusinessCostCenter = false,
	): Observable<EmployeeCapacityListElement[]> {
		const requestUrl =
			`${this.url}/${capacityParameters.viewType}/${capacityParameters.months}/` +
			`${moment(capacityParameters.startDate).format("MM_YYYY")}/${capacityParameters.planned}/` +
			`${capacityParameters.operativOnly}/${capacityParameters.concrete}`;
		const params = searchString
			? {
					businessUnits: `${businessUnits}`,
					divisions: `${divisions}`,
					departments: `${departments}`,
					statuses: `${statuses}`,
					includeBusinessCostCenter: `${includeBusinessCostCenter}`,
					searchString: `${searchString}`,
				}
			: {
					businessUnits: `${businessUnits}`,
					divisions: `${divisions}`,
					departments: `${departments}`,
					statuses: `${statuses}`,
					includeBusinessCostCenter: `${includeBusinessCostCenter}`,
				};
		const result = this.http.get<never[]>(requestUrl, {
			params,
			observe: "response",
		});
		return result.pipe(
			switchMap(async (value) => {
				const client = await this.customizingService.getCurrentClient();

				return await asyncMap(value.body, (result) => this.extractResult(result, client));
			}),
		);
	}

	public async getNNEmployeeCapacity(
		calculationVariant: MisplannedCapacityEnum,
		numberOfMonths: number,
		startMonth: string,
	): Promise<{
		[month: string]: number;
	}> {
		const requestUrl = `${this.urlNN}/${calculationVariant}/${numberOfMonths}/${startMonth}`;
		return (await firstValueFrom(this.getAll<never>(requestUrl))).body;
	}

	public async getEmployeeCapacitiesWithoutFixedprice(
		startDate: string,
		months: number,
		employeeIds: number[],
	): Promise<EmployeeRevenueListElement[]> {
		const requestUrl = `${this.url}/revenue/${months}/${startDate}`;
		const params = {employeeIds: `${employeeIds}`};
		return (
			await firstValueFrom(
				this.http.get<EmployeeRevenueListElement[]>(requestUrl, {
					params,
					observe: "response",
				}),
			)
		).body;
	}

	async extractResult(result, client: Client): Promise<EmployeeCapacityListElement> {
		delete result["assignmentsToProject"];
		delete result["capacity"];
		delete result["capacityExcel"];
		delete result["role"];

		return {
			employee: {
				...result.employee,
				businessUnit: result.employee.businessUnit,
				email: result.employee.email,
				personnelNumber: result.employee.personnelNumber,
				firstName: result.employee.firstName,
				lastName: result.employee.lastName,
				//files: result.employee.files,
				id: result.employee.id,
				humanResourcesManager: result.employee.humanResourcesManager,
				proportionOfWorkingTime: result.employee.proportionOfWorkingTime,
				dispoComment: result.employee.dispoComment,
				dispoCommentLastChangedAt: result.employee.dispoCommentLastChangedAt,
				dispoCommentLastChangedBy: result.employee.dispoCommentLastChangedBy,
				misplannedAnnotation: result.employee.misplannedAnnotation,
				commentLastChangedAt: result.employee.commentLastChangedAt,
				commentLastChangedBy: result.employee.commentLastChangedBy,
				annotation: result.employee.annotation,
				travelRestrictions: result.employee.travelRestrictions,
				jobVariant: result.employee.jobVariant,
				jobLevel: EnumMapper.jobLevelDisplayValue(result.employee.jobLevel, null),
				_links: result.employee._links,
				status: EnumMapper.statusEmployeeMapper([result.employee.status], this.translateService, false).toString(),
				leavingDate: result.employee.leavingDate,
				costCenter: result.employee.costCenter,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				business_CostCenter: result.employee.business_CostCenter,
				tag: result.employee.tag,
				notRelevantForCapa: result.employee.notRelevantForCapa,
				businessHierarchy: {
					division:
						result.employee.businessHierarchy != null
							? await this.divisionService.getDivisionLabel(result.employee.businessHierarchy.division)
							: result.employee.business_CostCenter
								? "N/A"
								: "",
					businessUnit:
						result.employee.businessHierarchy != null
							? await this.divisionService.getBusinessUnitByDivision(result.employee.businessHierarchy.division)
							: result.employee.business_CostCenter
								? "N/A"
								: "",
				},
				hierarchy: {
					department: result.employeeDepartment ?? (result.employee.costCenter ? "N/A" : ""),
				},
			},
			employeeName: result.employee.lastName + ", " + result.employee.firstName,
			skills: result.employee.skills || "",
			division: await this.divisionService.getDivisionLabel(result.employee.division),
			travelWillingness: EnumMapper.travelWillingnessMapper(
				[result.employee.travelWillingness],
				client,
				false,
			).toString(),
			travelWillingnessInternational: result.employee.travelWillingnessInternational,
			travelWillingnessCombined: EnumMapper.travelWillingnessCombinedMapper(
				[result.employee.travelWillingness],
				this.translateService,
				client,
				[result.employee.travelWillingnessInternational],
				false,
			).toString(),
			location: EnumMapper.locationMapper([result.employee.location], this.translateService, false).toString(),
			jobLevel: EnumMapper.jobLevelDisplayValue(result.employee.jobLevel, null),
			jobFamily: result.employee.jobFamily,
			employeeDivision: result.employee.division,
			department: result.employeeDepartment,
			professionalArea: result.employee.division,
			dispoFlag: result.employee.dispoFlag,
			country: result.employee.country,
			employeeCapacityInPersonDays: result.valueMap,
			freeCapacityInPersonDays: result.valueMap,
			employeeCapacityInPercent: result.freePersonDaysInPercent,
			vacation: result.vacation,
			availableCapacity: result.availableCapacity,
			projectLeaderNames: result.projectLeaderNames,
			divisionManagerNames: result.divisionManagerNames,
			isVisibleByMask: true,
			isVisibleByColor: true,
			hasValidActionPlan: result.hasValidActionPlan,
			skillsControl: new UntypedFormControl(result.employee.skills || ""),
			dispoCommentControl: new UntypedFormControl(result.employee.dispoComment),
			misplannedAnnotationControl: new UntypedFormControl(result.employee.misplannedAnnotation),
			annotationControl: new UntypedFormControl(result.employee.annotation),
			capacitySumInPT: parseFloat(this.sumValueMap(result.valueMap).toFixed(2)),
			resourceRequestCount: result.resourceRequestCount,
		};
	}

	private sumValueMap(valueMap: {[month: string]: number}): number {
		return Object.values(valueMap).reduce((a, b) => a + b, 0);
	}
}
